/**
 * Static User Configuration
 * 
 * This file contains the user database for the application.
 * Edit this file manually to add, remove, or modify users.
 * 
 * IMPORTANT: After editing this file, restart the development server
 * for changes to take effect.
 */

// Synchronous version for static initialization (and validation)
const hashPasswordSync = (password: string): string => {
  // For static initialization, use a simple hash algorithm
  // This is just for development - in production, use proper password hashing
  let hash = 0
  for (let i = 0; i < password.length; i++) {
    const char = password.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16)
}

// User role definitions
export type UserRole = 'admin' | 'user' | 'manager' | 'employee'

// User permissions by role
export const USER_PERMISSIONS = {
  admin: {
    dashboard: true,
    orders: true,
    timesheets: true,
    reports: true,
    settings: true,
    userManagement: true
  },
  user: {
    dashboard: true,
    orders: true,
    timesheets: true,
    reports: true,
    settings: false,
    userManagement: false
  },
  manager: {
    dashboard: true,
    orders: true,
    timesheets: true,
    reports: true,
    settings: true,
    userManagement: false
  },
  employee: {
    dashboard: false,
    orders: false,
    timesheets: true,
    reports: false,
    settings: false,
    userManagement: false
  }
} as const

// Static user database
export const STATIC_USERS = [
  {
    id: 1,
    username: 'jeff',
    email: 'jeff@zeiglerenvironmental.com',
    name: 'Jeff',
    role: 'admin' as UserRole,
    password_hash: hashPasswordSync('password123'), // Change this password!
    created_at: '2024-01-01T00:00:00Z',
    active: true
  },
  {
    id: 2,
    username: 'wade',
    email: 'zeiglerofficemail@gmail.com',
    name: 'Wade Eggleston',
    role: 'employee' as UserRole,
    password_hash: hashPasswordSync('timesheet2024'), // Change this password!
    created_at: '2024-01-01T00:00:00Z',
    active: true
  }
] as const

// Helper functions for user management
export const getUserByUsername = (username: string) => {
  return STATIC_USERS.find(user => user.username === username && user.active)
}

export const getUserById = (id: number) => {
  return STATIC_USERS.find(user => user.id === id && user.active)
}

export const validatePassword = (inputPassword: string, storedHash: string): boolean => {
  return hashPasswordSync(inputPassword) === storedHash
}

export const hasPermission = (role: UserRole, permission: keyof typeof USER_PERMISSIONS.admin): boolean => {
  return USER_PERMISSIONS[role][permission] || false
}

// Export type for use in other files
export interface StaticUser {
  id: number
  username: string
  email: string
  name: string
  role: UserRole
  password_hash: string
  created_at: string
  active: boolean
} 